import { useState, useMemo, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useTheme,
} from "@mui/material";

import AuthApi from "api/auth";
import StorageApi, { StorageBuckets } from "api/storage";
import CircularLoader from "components/CircularLoader";
import useNotifier from "hooks/useNotifier";
import useBoundStore from "store";

const randomCode = Math.floor(100000 + Math.random() * 900000).toString();

interface DeleteAccountDialogProps {
  open: boolean;
  onClose: () => void;
}

function DeleteAccountDialog(props: DeleteAccountDialogProps) {
  const { onClose, open } = props;
  const [loading, setLoading] = useState(false);
  const { control, handleSubmit, watch, reset } = useForm<{
    confirmationCode: string;
  }>({ defaultValues: { confirmationCode: "" } });
  const { showNotification } = useNotifier();
  const setUser = useBoundStore((state) => state.user.set);
  const userProfile = useBoundStore((state) => state.user.profile);
  const groups = useBoundStore((state) => state.appData.groups);
  const authApi = useMemo(() => new AuthApi(showNotification), [showNotification]);
  const storageApi = useMemo(() => new StorageApi(showNotification), [showNotification]);
  const theme = useTheme();

  const onSubmit = async () => {
    setLoading(true);

    // Delete profile avatar from storage
    if (userProfile?.avatar) {
      await storageApi.deleteFiles(StorageBuckets.PROFILES_AVATARS, [userProfile.avatar, `s-${userProfile.avatar}`]);
    }

    // Delete group avatars for groups where the user is the sole member
    // (those groups will be deleted server-side, so storage must be cleaned up beforehand)
    const soloGroups = groups.filter((g) => g.users.length === 1 && g.avatar);
    await Promise.all(
      soloGroups.map((g) => storageApi.deleteFiles(StorageBuckets.GROUPS_AVATARS, [g.avatar!, `s-${g.avatar!}`]))
    );

    const { error } = await authApi.deleteAccount();

    onClose();

    if (!error) {
      setUser({ auth: null, session: null });
    }
  };

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby='edit-user-data-dialog' fullWidth maxWidth='xs'>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete='off'>
        <DialogTitle id='edit-user-data-dialog'>Delete account</DialogTitle>
        <DialogContent>
          <DialogContentText gutterBottom>
            Be careful! This action can&#39;t be undone. Once you delete your account all your data will be lost and
            can&#39;t be retrieved. Enter the following code{" "}
            <b style={{ color: theme.palette.secondary.main }}>{randomCode}</b> to be able to delete your account.
          </DialogContentText>
          <Controller
            control={control}
            name='confirmationCode'
            render={({ field }) => (
              <TextField {...field} label='Code' size='small' margin='dense' autoFocus fullWidth />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <CircularLoader loading={loading}>
            <Button disabled={loading || watch("confirmationCode") !== randomCode} type='submit'>
              Delete
            </Button>
          </CircularLoader>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default DeleteAccountDialog;
